import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment.development';

/**
 * KohaAuthService — Manages authentication credentials for the Koha REST API.
 *
 * Currently implements HTTP Basic Auth using credentials from the environment
 * configuration. Designed as the single source of truth for auth state so
 * that a future migration to OAuth2 (client credentials grant) only requires
 * changing this one service.
 *
 * ⚠️  SECURITY NOTE: Basic Auth credentials are encoded (base64), NOT encrypted.
 *     For production, consider proxying through a backend (BFF pattern) or
 *     migrating to OAuth2 once RESTOAuth2ClientCredentials is enabled on Koha.
 *
 * Usage: Injected exclusively by the auth interceptor. Feature services
 * should NEVER import this directly — they use HttpClient, which flows
 * through the interceptor chain automatically.
 */
@Injectable({ providedIn: 'root' })
export class KohaAuthService {

  /** Cached base64 token — computed once, reused for all requests */
  private basicToken: string | null = null;

  /**
   * Returns the Authorization header value for Basic Auth.
   * Example: "Basic YW5hc3NlcjpBaG1uYXNAMTk3Ng=="
   *
   * @throws Error if credentials are not configured
   */
  getAuthorizationHeader(): string {
    if (!this.basicToken) {
      this.basicToken = this.buildBasicToken();
    }
    return `Basic ${this.basicToken}`;
  }

  /**
   * Check whether valid credentials are configured.
   */
  hasCredentials(): boolean {
    const auth = (environment as any).auth;
    return !!(auth?.username && auth?.password);
  }

  /**
   * Invalidate the cached credential token.
   * Called by the interceptor on 401 to force re-computation
   * (useful if credentials are ever refreshed at runtime).
   */
  invalidate(): void {
    this.basicToken = null;
  }

  private buildBasicToken(): string {
    const auth = (environment as any).auth;
    if (!auth?.username || !auth?.password) {
      throw new Error(
        '[KohaAuthService] Missing auth credentials in environment config. ' +
        'Ensure environment.auth.username and environment.auth.password are set.'
      );
    }
    // Never log raw credentials
    return btoa(`${auth.username}:${auth.password}`);
  }
}
